import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = { name: "", photo: "", role: "", services: [], active: true, commission: 0 };

export default function Barbers() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/barbers", { params: { all: true } }).then((r) => setItems(r.data || []));
  useEffect(() => { load(); api.get("/services", { params: { all: true } }).then((r) => setServices(r.data || [])); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...EMPTY, ...b }); setOpen(true); };

  const toggleService = (id) => {
    const has = form.services.includes(id);
    setForm({ ...form, services: has ? form.services.filter((s) => s !== id) : [...form.services, id] });
  };

  const save = async () => {
    const payload = { ...form, commission: Number(form.commission) };
    try {
      if (editing) await api.put(`/barbers/${editing.id}`, payload);
      else await api.post("/barbers", payload);
      toast.success("Barbeiro salvo"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/barbers/${id}`); toast.success("Removido"); load(); } catch { toast.error("Erro"); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Barbeiros</h1><p className="text-zinc-400 mt-1">Cadastre e administre a equipe.</p></div>
        <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full" onClick={openNew} data-testid="add-barber-btn"><Plus className="w-4 h-4 mr-1" /> Novo</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((b) => (
          <div key={b.id} className={`bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden ${b.active ? "" : "opacity-60"}`} data-testid={`barber-item-${b.id}`}>
            <div className="h-48 overflow-hidden">{b.photo ? <img src={b.photo} alt={b.name} className="w-full h-full object-cover" /> : <div className="w-full h-full pole-stripes opacity-30" />}</div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-white">{b.name}</h3>
                {!b.active && <span className="text-xs text-red-400">Inativo</span>}
              </div>
              <p className="text-sm text-amber-400">{b.role}</p>
              {b.commission > 0 && <p className="text-xs text-zinc-500 mt-1">Comissão: {b.commission}%</p>}
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="flex-1 border-zinc-700 text-zinc-300" onClick={() => openEdit(b)} data-testid={`edit-barber-${b.id}`}><Pencil className="w-4 h-4 mr-1" /> Editar</Button>
                <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => remove(b.id)} data-testid={`delete-barber-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing ? "Editar" : "Novo"} Barbeiro</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="barber-name" /></div>
            <div><Label className="text-zinc-300">Especialidade / Cargo</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="barber-role" /></div>
            <ImageUpload value={form.photo} onChange={(url) => setForm({ ...form, photo: url })} aspect={3 / 4} label="Foto do barbeiro" testid="barber-photo" previewClass="h-56" />
            <div><Label className="text-zinc-300">Comissão (%)</Label><Input type="number" value={form.commission} onChange={(e) => setForm({ ...form, commission: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="barber-commission" /></div>
            <div>
              <Label className="text-zinc-300">Serviços que realiza</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {services.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                    <Checkbox checked={form.services.includes(s.id)} onCheckedChange={() => toggleService(s.id)} data-testid={`barber-service-${s.id}`} /> {s.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="barber-active" /><Label className="text-zinc-300">Ativo</Label></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-amber-400 text-black font-bold" data-testid="save-barber-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
